import React, { useEffect, useState } from 'react';
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import { Card, CardBody, Alert, Badge, Row, Col, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from 'reactstrap';
import axios from 'axios';

const AdminCancellations = () => {
    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({
        open: false,
        type: 'success',
        content: 'Messages here.',
    });
    const [cancellations, setCancellations] = useState<any[]>([]);
    const [analytics, setAnalytics] = useState<any>({
        total_cancellations: 0,
        requested: 0,
        processing: 0,
        completed: 0,
        rejected: 0,
        subscription_cancels: 0,
        refund_requests: 0,
    });
    const [filters, setFilters] = useState({
        status: 'all',
        cancellation_type: 'all',
        search: '',
        date_from: '',
        date_to: '',
    });
    const [refundModal, setRefundModal] = useState<{
        open: boolean;
        item: any | null;
        cancellation: any | null;
    }>({
        open: false,
        item: null,
        cancellation: null,
    });
    const [refundQuantity, setRefundQuantity] = useState<number>(1);
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [processingRefund, setProcessingRefund] = useState(false);

    const handleAlertMsggClose = () => {
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const fetchCancellations = async () => {
        try {
            setshowloader(true);
            const response = await axios.post(
                '/payments/admin/cancellations/',
                filters,
                { withCredentials: true }
            );

            if (response.status === 'SENT') {
                setCancellations(response.cancellations);
                setAnalytics(response.analytics);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('Error fetching cancellations:', error);
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: error.response?.error || 'Error occurred',
                        type: 'error',
                    });
                } else if (error.response.status === 403) {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: 'Access denied or session expired!',
                        type: 'error',
                    });
                    setTimeout(() => {
                        window.location.href = '/logout';
                    }, 2000);
                } else {
                    setAlertMsgg({
                        ...alertMsgg,
                        open: true,
                        content: 'An error occurred',
                        type: 'error',
                    });
                }
            } else {
                setAlertMsgg({
                    ...alertMsgg,
                    open: true,
                    content: 'Network error',
                    type: 'error',
                });
            }
            setshowloader(false);
        }
    };

    useEffect(() => {
        fetchCancellations();
    }, []);

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));

        // Auto-apply filters when status radio button changes
        if (name === 'status' || name === 'cancellation_type') {
            const newFilters = { ...filters, [name]: value };
            applyFiltersWithData(newFilters);
        }
    };

    const applyFiltersWithData = async (filtersToApply: typeof filters) => {
        try {
            setshowloader(true);
            const response = await axios.post(
                '/payments/admin/cancellations/',
                filtersToApply,
                { withCredentials: true }
            );
            if (response.status === 'SENT') {
                setCancellations(response.cancellations);
                setAnalytics(response.analytics);
            }
            setshowloader(false);
        } catch (error: any) {
            console.error('Error fetching cancellations:', error);
            setAlertMsgg({
                ...alertMsgg,
                open: true,
                content: 'An error occurred while fetching cancellations',
                type: 'error',
            });
            setshowloader(false);
        }
    };

    const handleApplyFilters = () => {
        fetchCancellations();
    };

    const handleClearFilters = () => {
        setFilters({
            status: 'all',
            cancellation_type: 'all',
            search: '',
            date_from: '',
            date_to: '',
        });
        setTimeout(() => fetchCancellations(), 100);
    };

    const handleOpenRefundModal = (item: any, cancellation: any) => {
        setRefundQuantity(item.quantity_to_cancel);
        setRefundAmount(item.refund_amount);
        setRefundModal({ open: true, item, cancellation });
    };

    const handleCloseRefundModal = () => {
        setRefundModal({ open: false, item: null, cancellation: null });
        setRefundQuantity(1);
        setRefundAmount(0);
    };

    const handleProcessRefund = async () => {
        if (!refundModal.item) return;

        try {
            setProcessingRefund(true);
            const payload: any = {};
            
            // If admin changed quantity, send it
            if (refundQuantity !== refundModal.item.quantity_to_cancel) {
                payload.quantity = refundQuantity;
            }
            
            // If admin changed refund amount, send it
            if (refundAmount !== refundModal.item.refund_amount) {
                payload.refund_amount = refundAmount;
            }

            const response = await axios.post(
                `/payments/admin/cancellation-item/${refundModal.item.id}/refund/`,
                payload,
                { withCredentials: true }
            );

            if (response.status === 'SENT') {
                setAlertMsgg({
                    ...alertMsgg,
                    open: true,
                    content: response.message || 'Refund processed successfully!',
                    type: 'success',
                });
                handleCloseRefundModal();
                fetchCancellations(); // Refresh the list
            }
        } catch (error: any) {
            console.error('Error processing refund:', error);
            setAlertMsgg({
                ...alertMsgg,
                open: true,
                content: error.response?.data?.error || 'Failed to process refund',
                type: 'error',
            });
        } finally {
            setProcessingRefund(false);
        }
    };

    // Calculate refund amount when quantity changes
    const handleQuantityChange = (newQuantity: number) => {
        setRefundQuantity(newQuantity);
        if (refundModal.item) {
            // Calculate based on unit price (refund_amount / quantity_to_cancel gives unit price)
            const unitPrice = refundModal.item.refund_amount / refundModal.item.quantity_to_cancel;
            setRefundAmount(unitPrice * newQuantity);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'requested':
                return <Badge color="warning">Requested</Badge>;
            case 'processing':
                return <Badge color="info">Processing</Badge>;
            case 'completed':
                return <Badge color="success">Completed</Badge>;
            case 'rejected':
                return <Badge color="danger">Rejected</Badge>;
            default:
                return <Badge color="secondary">{status}</Badge>;
        }
    };

    const getTypeBadge = (type: string) => {
        switch (type) {
            case 'subscription_cancel':
                return <Badge color="primary">Subscription Cancel</Badge>;
            case 'refund_request':
                return <Badge color="info">Refund Request</Badge>;
            default:
                return <Badge color="secondary">{type}</Badge>;
        }
    };

    const PRODUCT_TYPE_LABELS: Record<string, string> = {
        plan: 'Plan/Package',
        planoptionals: 'Optional',
        addon: 'Add-On',
        subscriptionaddon: 'Subscription',
        onetimeproduct: 'Product/Service',
    };

    const REASON_LABELS: Record<string, string> = {
        changed_mind: 'Changed my mind',
        found_alternative: 'Found a better alternative',
        too_expensive: 'Too expensive',
        not_expected: 'Not what I expected',
        technical_issues: 'Technical issues',
        poor_service: 'Poor customer service',
        no_longer_needed: 'No longer needed',
        accidental_purchase: 'Accidental purchase',
        duplicate_order: 'Duplicate order',
        service_not_described: 'Service not as described',
        other: 'Other',
    };

    document.title = 'Cancellations | Admin | BizServe';

    return (
        <div className="page-content">
            {alertMsgg.open && (
                <Alert
                    color={alertMsgg.type === 'success' ? 'success' : alertMsgg.type === 'warning' ? 'warning' : 'danger'}
                    toggle={handleAlertMsggClose}
                    fade={false}
                    style={{ fontWeight: 'bold' }}
                >
                    {alertMsgg.content}
                </Alert>
            )}

            <div className="loaderContainer" hidden={!showloader}>
                <div className="spinner">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>

            <div className="container-fluid px-0 px-md-3 py-3" style={{ minHeight: '90vh' }}>
                <Breadcrumbs title="Admin" breadcrumbItem="Cancellation Requests" />

                {/* Analytics Cards */}
                <Row className="mb-4">
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #5558d8 0%, #5a3d83 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>
                                            Total Cancellations
                                        </p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.total_cancellations}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-x-circle font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>
                                            Pending Requests
                                        </p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.requested}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-time font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>
                                            Completed Requests
                                        </p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.completed}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-check-circle font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3} md={6} className="mb-3 mb-xl-0">
                        <Card className="rounded-4 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <p className="text-white mb-1 fw-semibold" style={{ fontSize: 13, opacity: 0.9 }}>
                                            Total Refund Requests
                                        </p>
                                        <h3 className="mb-0 text-white fw-bold">{analytics.refund_requests}</h3>
                                    </div>
                                    <div className="avatar-sm">
                                        <span className="avatar-title bg-white bg-opacity-25 rounded-circle text-white">
                                            <i className="bx bx-dollar-circle font-size-24"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Filters and Cancellations Section */}
                <div className="row g-4">
                    {/* Filters Card */}
                    <div className="col-12 col-lg-3">
                        <Card className="rounded-4 border-0 shadow-sm bg-white">
                            <CardBody className="p-4">
                                <div className="mb-3 fw-bold fs-5 text-primary-emphasis d-flex align-items-center">
                                    <i className="bx bx-filter-alt me-2"></i>
                                    Filters
                                </div>

                                <div className="mb-3">
                                    <Label className="form-label fw-semibold d-block mb-2">Status</Label>
                                    <div className="d-flex flex-column gap-2">
                                        {['all', 'requested', 'processing', 'completed', 'rejected'].map((statusOption) => (
                                            <div className="form-check" key={statusOption}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="status"
                                                    id={`status_${statusOption}`}
                                                    value={statusOption}
                                                    checked={filters.status === statusOption}
                                                    onChange={handleFilterChange}
                                                />
                                                <label className="form-check-label" htmlFor={`status_${statusOption}`}>
                                                    {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <Label className="form-label fw-semibold d-block mb-2">Type</Label>
                                    <div className="d-flex flex-column gap-2">
                                        {['all', 'subscription_cancel', 'refund_request'].map((typeOption) => (
                                            <div className="form-check" key={typeOption}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="cancellation_type"
                                                    id={`type_${typeOption}`}
                                                    value={typeOption}
                                                    checked={filters.cancellation_type === typeOption}
                                                    onChange={handleFilterChange}
                                                />
                                                <label className="form-check-label" htmlFor={`type_${typeOption}`}>
                                                    {typeOption === 'all' ? 'All Types' : typeOption === 'subscription_cancel' ? 'Subscription Cancel' : 'Refund Request'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="search" className="form-label fw-semibold">
                                        Search
                                    </Label>
                                    <Input
                                        type="text"
                                        id="search"
                                        name="search"
                                        className="form-control"
                                        placeholder="Order ID, Email..."
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="date_from" className="form-label fw-semibold">
                                        Date From
                                    </Label>
                                    <Input
                                        type="date"
                                        id="date_from"
                                        name="date_from"
                                        className="form-control"
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="mb-3">
                                    <Label htmlFor="date_to" className="form-label fw-semibold">
                                        Date To
                                    </Label>
                                    <Input
                                        type="date"
                                        id="date_to"
                                        name="date_to"
                                        className="form-control"
                                        value={filters.date_to}
                                        onChange={handleFilterChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="d-flex gap-2 flex-column">
                                    <Button color="primary" className="w-100 btn-rounded" onClick={handleApplyFilters}>
                                        <i className="bx bx-search me-1"></i> Apply Filters
                                    </Button>
                                    <Button color="secondary" outline className="w-100 btn-rounded" onClick={handleClearFilters}>
                                        <i className="bx bx-x me-1"></i> Clear
                                    </Button>
                                </div>

                                <hr className="my-4" />

                                <div className="mb-2">
                                    <small className="text-muted fw-semibold d-block mb-2">Additional Stats</small>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Processing:</span>
                                        <span className="badge bg-info">{analytics.processing}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Rejected:</span>
                                        <span className="badge bg-danger">{analytics.rejected}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Subscription Cancels:</span>
                                        <span className="badge bg-primary">{analytics.subscription_cancels}</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Cancellations List Card */}
                    <div className="col-12 col-lg-9">
                        <Card className="rounded-4 border-0 shadow-lg bg-white" style={{ minHeight: 600 }}>
                            <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h5 className="mb-1 fw-bold">All Cancellation Requests</h5>
                                        <p className="text-muted mb-0">Total: {cancellations.length} request(s)</p>
                                    </div>
                                </div>

                                {cancellations.length === 0 && !showloader ? (
                                    <div className="text-center py-5">
                                        <div className="avatar-lg mx-auto mb-4">
                                            <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                                <i className="bx bx-info-circle font-size-40"></i>
                                            </div>
                                        </div>
                                        <h5 className="text-muted">No cancellation requests found</h5>
                                        <p className="text-muted">Try adjusting your filters</p>
                                    </div>
                                ) : (
                                    <div className="cancellations-list" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                                        {cancellations.map((cancellation: any, idx: number) => (
                                            <div
                                                key={cancellation.id || idx}
                                                className="mb-4 p-4 rounded-4 shadow-sm"
                                                style={{ background: 'linear-gradient(120deg, #f3f4f6 60%, #e5e7eb 100%)', border: '1px solid #e5e7eb' }}
                                            >
                                                {/* Cancellation Header */}
                                                <div className="d-flex flex-wrap align-items-start justify-content-between mb-3 pb-3 border-bottom">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <h6 className="mb-0 fw-bold">Cancellation #{cancellation.id}</h6>
                                                            {getStatusBadge(cancellation.status)}
                                                            {getTypeBadge(cancellation.cancellation_type)}
                                                        </div>
                                                        <div className="text-muted small">
                                                            Order ID: <span className="fw-semibold">{cancellation.order_uuid}</span> | Total: $
                                                            {cancellation.order_total?.toFixed(2)}
                                                        </div>
                                                        <div className="text-muted small">
                                                            Customer: <span className="fw-semibold">{cancellation.user_name}</span> ({cancellation.user_email})
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="text-muted small">Requested</div>
                                                        <div className="fw-semibold">{formatDate(cancellation.requested_at)}</div>
                                                        {cancellation.processed_at && (
                                                            <>
                                                                <div className="text-muted small mt-2">Processed</div>
                                                                <div className="fw-semibold">{formatDate(cancellation.processed_at)}</div>
                                                                {cancellation.processed_by_name && (
                                                                    <div className="text-muted small">By: {cancellation.processed_by_name}</div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Cancellation Reason */}
                                                <div className="mb-3 p-3 rounded bg-white">
                                                    <div className="text-muted small fw-semibold mb-1">Cancellation Reason:</div>
                                                    <div className="fw-semibold">{REASON_LABELS[cancellation.reason] || cancellation.reason}</div>
                                                    {cancellation.custom_reason && (
                                                        <div className="text-muted fst-italic mt-1">"{cancellation.custom_reason}"</div>
                                                    )}
                                                </div>

                                                {/* Customer Notes */}
                                                {cancellation.notes && (
                                                    <div className="mb-3 p-3 rounded bg-white">
                                                        <div className="text-muted small fw-semibold mb-1">Customer Notes:</div>
                                                        <div>{cancellation.notes}</div>
                                                    </div>
                                                )}

                                                {/* Admin Notes */}
                                                {cancellation.admin_notes && (
                                                    <div className="mb-3 p-3 rounded bg-warning bg-opacity-10 border border-warning">
                                                        <div className="text-dark small fw-semibold mb-1">
                                                            <i className="bx bx-lock me-1"></i>Admin Notes:
                                                        </div>
                                                        <div className="text-dark">{cancellation.admin_notes}</div>
                                                    </div>
                                                )}

                                                {/* Cancelled Items */}
                                                {cancellation.items && cancellation.items.length > 0 && (
                                                    <div>
                                                        <div className="fw-semibold mb-2">Cancelled Items:</div>
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-sm align-middle mb-0 bg-white">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Product</th>
                                                                        <th>Type</th>
                                                                        <th className="text-center">Quantity</th>
                                                                        <th className="text-end">Refund Amount</th>
                                                                        <th className="text-center">Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {cancellation.items.map((item: any, itemIdx: number) => (
                                                                        <tr key={item.id || itemIdx}>
                                                                            <td className="fw-semibold">{item.product_title}</td>
                                                                            <td>
                                                                                <Badge color="info" className="text-dark">
                                                                                    {PRODUCT_TYPE_LABELS[item.product_type] || item.product_type}
                                                                                </Badge>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <Badge color="secondary">{item.quantity_to_cancel}</Badge>
                                                                            </td>
                                                                            <td className="text-end fw-semibold text-success">
                                                                                ${item.refund_amount?.toFixed(2) || '0.00'}
                                                                                {item.stripe_refund_id && (
                                                                                    <div className="text-muted small mt-1">
                                                                                        Refund ID: {item.stripe_refund_id}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {getStatusBadge(item.line_status)}
                                                                                {item.product_type === 'onetimeproduct' && 
                                                                                 item.line_status === 'requested' && 
                                                                                 !item.stripe_refund_id && (
                                                                                    <div className="mt-2">
                                                                                        <Button
                                                                                            color="success"
                                                                                            size="sm"
                                                                                            onClick={() => handleOpenRefundModal(item, cancellation)}
                                                                                        >
                                                                                            <i className="bx bx-dollar-circle me-1"></i>
                                                                                            Process Refund
                                                                                        </Button>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Total Refund */}
                                                <div className="mt-3 pt-3 border-top">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-semibold">Total Potential Refund:</span>
                                                        <span className="fw-bold text-success fs-5">
                                                            $
                                                            {cancellation.items
                                                                ?.reduce((sum: number, item: any) => sum + (item.refund_amount || 0), 0)
                                                                .toFixed(2) || '0.00'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Refund Processing Modal */}
            <Modal isOpen={refundModal.open} toggle={handleCloseRefundModal} size="md" centered>
                <ModalHeader toggle={handleCloseRefundModal}>
                    Process Refund
                </ModalHeader>
                <ModalBody>
                    {refundModal.item && (
                        <>
                            <div className="mb-3">
                                <div className="p-3 bg-light rounded">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Product:</span>
                                        <span className="fw-semibold">{refundModal.item.product_title}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Original Quantity:</span>
                                        <span className="fw-semibold">{refundModal.item.quantity_to_cancel}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Original Refund Amount:</span>
                                        <span className="fw-semibold text-success">${refundModal.item.refund_amount?.toFixed(2) || '0.00'}</span>
                                    </div>
                                </div>
                            </div>

                            <FormGroup>
                                <Label className="fw-semibold">
                                    Quantity to Refund <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={refundModal.item.quantity_to_cancel}
                                    value={refundQuantity}
                                    onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                                />
                                <small className="text-muted">
                                    Maximum: {refundModal.item.quantity_to_cancel} (original requested quantity)
                                </small>
                            </FormGroup>

                            <FormGroup>
                                <Label className="fw-semibold">
                                    Refund Amount ($) <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="number"
                                    min={0.01}
                                    step="0.01"
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                                />
                                <small className="text-muted">
                                    This is the amount that will be refunded via Stripe
                                </small>
                            </FormGroup>

                            <Alert color="info" className="mt-3">
                                <i className="bx bx-info-circle me-2"></i>
                                <strong>Note:</strong> The refund will be processed immediately via Stripe. 
                                Make sure the amount is correct before proceeding.
                            </Alert>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCloseRefundModal} disabled={processingRefund}>
                        Cancel
                    </Button>
                    <Button 
                        color="success" 
                        onClick={handleProcessRefund} 
                        disabled={processingRefund || refundAmount <= 0 || refundQuantity <= 0}
                    >
                        {processingRefund ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-check-circle me-2"></i>
                                Process Refund
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default AdminCancellations;

